import { getChainInfo, SupportedNetwork } from '@spritz-finance/sdk'
import { Body, Button, Collapsible, CollapsibleMethods, colors, Spacer } from '@spritz-finance/ui'
import dayjs from 'dayjs'
import { useRef, useState } from 'react'
import styled from 'styled-components'
import { ExpandDetailIcon } from '../../../../../../components/ExpandedDetailIcon'
import { Row } from '../../../../../../components/Layout/Row'
import { cssVar } from '../../../../../../theme/utils'
import { formatCurrency } from '../../../../../../utils/format'
import { NetworkLogo } from '../../../../../../web3/networkSwitcher/NetworkLogo'
import { maskedAddress } from '../../../../../../web3/utils/maskedAddress'
import usdcLogo from '../../../../../../assets/img/usdc.svg'
import { Subscription } from '../../Bills'
import { SmartPayLogo } from '../SmartPayLogo'
import { getOrdinal } from '../utils'

const DetailsContainer = styled.div`
    border: 1px solid var(--text-secondary, ${colors.ltGrey});
    border-radius: 12px;
    padding: 16px;
`

const Logo = styled.img`
    width: 24px;
`

const ClickableRow = styled.div`
    display: flex;
    flex: 1;
    justify-content: center;
    align-items: center;
    &:hover {
        cursor: pointer;
    }
`

export const SubscriptionDetails: React.FC<{ subscription: Subscription }> = ({
    subscription,
    children,
}) => {
    const network = subscription.network as SupportedNetwork
    const chainInfo = getChainInfo(network)
    const dayOfMonth = dayjs(subscription.startDate).date()
    const ordinalDay = `${dayOfMonth}${getOrdinal(dayOfMonth)}`
    const totalPayments = subscription.totalPayments

    return (
        <DetailsContainer>
            <Row align="space-between">
                <Body.Med size={14}>Recurring payment amount:</Body.Med>
                <Body.Med size={14}>{formatCurrency(subscription.amount)}</Body.Med>
            </Row>
            <Spacer height={8} />
            <Spacer height={4} />
            <Row align="space-between">
                <Body.Med size={14}>Pay with:</Body.Med>
                <Row>
                    <Logo src={usdcLogo} />
                    <Spacer width={4} />
                    <Body.Med size={14}>USDC</Body.Med>
                </Row>
            </Row>
            <Spacer height={8} />
            <Spacer height={4} />
            <Row align="space-between">
                <Body.Med size={14}>Network:</Body.Med>
                <Row>
                    <NetworkLogo network={network} />
                    <Spacer width={4} />
                    <Body.Med size={14}>{chainInfo.name}</Body.Med>
                </Row>
            </Row>
            <Spacer height={8} />
            <Spacer height={4} />
            <Row align="space-between">
                <Body.Med size={14}>Wallet:</Body.Med>
                <Body.Med size={14}>{maskedAddress(subscription.walletAddress)}</Body.Med>
            </Row>
            <Spacer height={8} />
            <Spacer height={4} />
            <Row align="space-between">
                <Body.Med size={14}>Payment date:</Body.Med>
                <Body.Med size={14}>{`On the ${ordinalDay} of every month`}</Body.Med>
            </Row>
            <Spacer height={8} />
            <Spacer height={4} />
            {totalPayments > 0 && (
                <>
                    <Row align="space-between">
                        <Body.Med size={14}>Payments remaining:</Body.Med>
                        <Body.Med size={14}>
                            {subscription.totalPayments - subscription.subscriptionCount}
                        </Body.Med>
                    </Row>
                    <Spacer height={8} />
                    <Spacer height={4} />
                </>
            )}
            <Row align="space-between">
                <Body.Med size={14}>Next payment date:</Body.Med>
                <Body.Med size={14}>
                    {dayjs(subscription.nextSubscriptionDate).format('MMM DD, YYYY')}
                </Body.Med>
            </Row>
            {children && (
                <>
                    <Spacer height={8} />
                    <Row align="center">{children}</Row>
                </>
            )}
        </DetailsContainer>
    )
}

export const CurrentSubscription = ({
    subscription,
    onClickManage,
}: {
    subscription: Subscription
    onClickManage: () => void
}) => {
    const defaultExpanded = false
    const [expanded, setExpanded] = useState(defaultExpanded)
    const collapible = useRef<CollapsibleMethods>(null)

    const toggle = () => {
        setExpanded((curr) => !curr)
        collapible.current?.toggle()
    }

    return (
        <>
            <Row align="center">
                <ClickableRow onClick={toggle}>
                    <SmartPayLogo />
                    <Spacer width={4} />
                    <Body.SmBold size={14}>SMARTPay is active</Body.SmBold>
                    <Spacer width={4} />
                    <ExpandDetailIcon
                        size={24}
                        active={expanded}
                        tint={cssVar('--text-secondary', colors.charcoal)}
                    />
                </ClickableRow>
            </Row>
            <Spacer height={8} />
            <Collapsible ref={collapible} defaultOpen={defaultExpanded}>
                <Spacer height={8} />
                <SubscriptionDetails subscription={subscription}>
                    <Button.Text onClick={onClickManage}>Manage recurring payment</Button.Text>
                </SubscriptionDetails>
                <Spacer height={8} />
            </Collapsible>
            <Spacer height={16} />
        </>
    )
}
